import { SignUp as ClerkSignup } from "@clerk/clerk-react"
import {
  Container,
  Grid,
  Stack,
  Title,
  Text,
  List,
  ThemeIcon,
  Box,
  rem,
  Image,
  Group,
  Center,
} from "@mantine/core"
import { IconCheck } from "@tabler/icons-react"

import { useBrand } from "@costory/front/lib/whitelabel"

/**
 * Marketing content displayed on the sign-up page for Costory brand
 */
function MarketingContent({ brand }: { brand: ReturnType<typeof useBrand> }) {
  return (
    <>
      {/* Mobile header - Logo and tagline */}
      <Stack gap="md" mb="xl" display={{ base: "block", md: "none" }}>
        <a href={brand.websiteUrl} target="_blank" rel="noreferrer">
          <Group gap="xs" align="center" justify="center">
            <Image
              src={brand.authLogo}
              alt={brand.name}
              h={60}
              w="auto"
              fit="contain"
            />
            <Text
              size={rem(44)}
              fw={700}
              style={{ color: "#000000", letterSpacing: "-0.5px" }}
            >
              {brand.name === "Tsadei" ? "Costory" : brand.name}
            </Text>
          </Group>
        </a>

        <Text size="xl" fw={700} ta="center" mt="md">
          {brand.tagline}
        </Text>
      </Stack>

      <Grid gutter="xl" align="center" style={{ width: "100%" }}>
        <Grid.Col
          span={{ base: 12, md: 6 }}
          display={{ base: "none", md: "block" }}
        >
          <Stack gap="lg">
            <a href={brand.websiteUrl} target="_blank" rel="noreferrer">
              <Group gap="xs" align="center">
                <Image
                  src={brand.authLogo}
                  alt={brand.name}
                  h={65}
                  w="auto"
                  fit="contain"
                />
                <Text
                  size={rem(44)}
                  fw={700}
                  style={{ color: brand.primaryColor, letterSpacing: "-0.5px" }}
                >
                  {brand.name === "Tsadei" ? "Costory" : brand.name}
                </Text>
              </Group>
            </a>

            <Stack gap="md">
              <Title order={2} size={rem(32)} fw={700}>
                {brand.tagline}
                <Text
                  component="span"
                  inherit
                  style={{ display: "block" }}
                  c="dimmed"
                >
                  Focus on building.
                </Text>
              </Title>

              <Text size="lg" c="dimmed">
                Engineers don&apos;t want to do FinOps. Costory automates the
                grunt work:
              </Text>
            </Stack>

            <Stack gap="md">
              <List
                spacing="md"
                icon={
                  <ThemeIcon color="green" size={24} radius="xl">
                    <IconCheck size={16} stroke={3} />
                  </ThemeIcon>
                }
              >
                <List.Item>
                  <Text fw={500}>Skip manual reporting</Text>
                  <Text size="sm" c="dimmed">
                    no more pulling billing data from AWS, GCP, or Azure.
                  </Text>
                </List.Item>

                <List.Item>
                  <Text fw={500}>See the &quot;why&quot; behind spend</Text>
                  <Text size="sm" c="dimmed">
                    Correlate spending with deployments, traffic, and business
                    events
                  </Text>
                </List.Item>

                <List.Item>
                  <Text fw={500}>Catch slow, compounding increases</Text>
                  <Text size="sm" c="dimmed">
                    automate the endless group-bys and filters and only get
                    pinged when costs truly change.
                  </Text>
                </List.Item>
              </List>
            </Stack>

            <Stack gap="md">
              <Stack gap="sm">
                <Text size="md" c="dimmed" fs="italic">
                  Trusted by engineering teams who care about ROI
                </Text>

                <Group gap="xl" mb="md">
                  <Image
                    src="img/bereal.png"
                    alt="BeReal"
                    h={25}
                    w="auto"
                    fit="contain"
                  />
                  <Image
                    src="img/alan.png"
                    alt="Alan"
                    h={25}
                    w="auto"
                    fit="contain"
                  />
                  <Image
                    src="img/pennylane.png"
                    alt="Pennylane"
                    h={25}
                    w="auto"
                    fit="contain"
                  />
                </Group>
              </Stack>

              <Stack gap={4}>
                <Text size="sm" fw={600}>
                  30-day free trial - No credit card required
                </Text>
                <Text size="sm" fw={600}>
                  Setup in hours, not days
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Grid.Col>

        {/* Right side - Clerk SignUp */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ClerkSignup
              routing="hash"
              forceRedirectUrl="/setup-domain"
              appearance={{
                elements: {
                  logoBox: {
                    display: "none",
                  },
                },
              }}
            />
          </Box>
        </Grid.Col>
      </Grid>

      {/* Mobile footer - Trusted by section */}
      <Stack gap="md" mt="xl" display={{ base: "block", md: "none" }}>
        <Stack gap="sm">
          <Text size="md" c="dimmed" fs="italic" ta="center">
            Trusted by engineering teams who care about ROI
          </Text>

          <Group gap="xl" justify="center" mb="md">
            <Image
              src="img/bereal.png"
              alt="BeReal"
              h={25}
              w="auto"
              fit="contain"
            />
            <Image
              src="img/alan.png"
              alt="Alan"
              h={25}
              w="auto"
              fit="contain"
            />
            <Image
              src="img/pennylane.png"
              alt="Pennylane"
              h={25}
              w="auto"
              fit="contain"
            />
          </Group>
        </Stack>

        <Stack gap={4}>
          <Text size="sm" fw={600} ta="center">
            30-day free trial - No credit card required
          </Text>
          <Text size="sm" fw={600} ta="center">
            Setup in hours, not days
          </Text>
        </Stack>
      </Stack>
    </>
  )
}

/**
 * Simplified sign-up page for whitelabel deployments without marketing content
 */
function WhitelabelSignUp({ brand }: { brand: ReturnType<typeof useBrand> }) {
  return (
    <Center mih="100vh" bg="surfaceGround">
      <Stack align="center" gap="xl">
        <a href={brand.websiteUrl} target="_blank" rel="noreferrer">
          <Group gap="xs" align="center" justify="center">
            <Image
              src={brand.authLogo}
              alt={brand.name}
              h={80}
              w="auto"
              fit="contain"
            />
            <Text
              size={rem(44)}
              fw={700}
              style={{ color: brand.primaryColor, letterSpacing: "-0.5px" }}
            >
              {brand.name}
            </Text>
          </Group>
        </a>

        {brand.tagline && (
          <Text size="xl" fw={700} ta="center" c="textSecondary">
            {brand.tagline}
          </Text>
        )}

        <Box>
          <ClerkSignup
            routing="hash"
            forceRedirectUrl="/setup-domain"
            appearance={{
              elements: {
                logoBox: {
                  display: "none",
                },
              },
            }}
          />
        </Box>
      </Stack>
    </Center>
  )
}

export function SignUp() {
  const brand = useBrand()

  // For whitelabel deployments without marketing content, show simplified sign-up
  if (!brand.showMarketingContent) {
    return <WhitelabelSignUp brand={brand} />
  }

  // Full marketing sign-up page for Costory brand
  return (
    <Container
      size="xl"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <MarketingContent brand={brand} />
    </Container>
  )
}
